import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { utils, comnUtils } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, Tab } from "@/comn/components"; // 화면 구성 컴포넌트
import {
    useFetch,
    useForm,
    useGrid,
    useStore,
    useToast,
    useResource,
    useModal,
    usePage,
    usePopup,
    useTab,
} from "@/comn/hooks";
import {
    APIS,
    SF_USR_APLY_INFO,
    SG_CO_TP_APLY_LIST,
    SF_CO_TP_DETAIL_INFO,
    FIELD_VISIBILITY_MAP,
    ATCH_FILE_VISIBILITY_MAP,
    CO_CD_INPUT_TRGT_SET,
    CO_CD_INPUT_PROPERTIES,
    SG_CO_EMP_LIST,
    SF_CO_EMP_INFO,
    SG_CO_HIST_LIST,
    SF_CO_HIST_INFO,
    SF_CO_DCLT_INFO,
    SG_CO_DCLT_LIST,
    URLS,
    CO_CD_LCNE_SET,
    SG_CO_EMP_CO_CD_LIST,
} from "@/tra/tancis/ptli/usr/co/services/PtliCoService";

import { getNameOfJSDocTypedef } from "typescript";

/*
 * Company Information Management Details
 * !== 업체 관리 상세 ==!
 */
export const PTLI0905004Q = (): JSX.Element => {
    const { pgeUid } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation();
    const navigate = useNavigate();
    const modal = useModal();
    const toast = useToast();
    const { coTin } = useParams();
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid });

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0011" },
            { area: "comnCd", comnCd: "LCM0006" },
            { area: "comnCd", comnCd: "CGM0095" },
            { area: "comnCd", comnCd: "CGM0096" },
            { area: "comnCd", comnCd: "PTL0001" },
            { area: "comnCd", comnCd: "LCM0006" },
            { area: "cstmCd" },
            { area: "cntyCd" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // User Information !== 사용자(대표자) 정보 ==!
        usrAplyInfo: useForm({ defaultSchema: SF_USR_APLY_INFO }),
        coTpAplyInfo: useForm({ defaultSchema: SF_CO_TP_DETAIL_INFO }),
        coEmpInfo: useForm({ defaultSchema: SF_CO_EMP_INFO }),
        coHistInfo: useForm({ defaultSchema: SF_CO_HIST_INFO }),
        coDcltInfo: useForm({ defaultSchema: SF_CO_DCLT_INFO }),
    };

    // Organization, Department Tab !== 기관,부서 탭 ==!
    const tab = useTab({
        defaultSchema: {
            id: "tab",
            schema: [
                { label: "L_CO_INFO" },
                { label: "L_CO_CD" },
                { label: "L_CO_EMP" },
                { label: "L_HIST" },
                { label: "L_DCLT" },
            ],
        },
    });

    const grid = {
        // Company Type List !== 업체 유형 목록 ==!
        coTpList: useGrid({
            defaultSchema: SG_CO_TP_APLY_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        coEmpList: useGrid({
            defaultSchema: SG_CO_EMP_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        coHistList: useGrid({
            defaultSchema: SG_CO_HIST_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        coDcltList: useGrid({
            defaultSchema: SG_CO_DCLT_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        coEmpCoCdList: useGrid({ defaultSchema: SG_CO_EMP_CO_CD_LIST }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get License User Information !== 업체 사용자 정보 조회 ==!
        getCoInfoMgmt: useFetch({
            api: () => APIS.getCoInfoMgmt(coTin),
            onSuccess: (data) => {
                grid.coTpList.clearData();
                grid.coEmpList.clearData();
                grid.coHistList.clearData();

                const coInfoMgmt = data.coInfoMgmt.content;
                form.usrAplyInfo.setValues(coInfoMgmt);
                grid.coTpList.setData({ content: coInfoMgmt.coTpList });
                grid.coEmpList.setData({ content: coInfoMgmt.coEmpList });
                grid.coHistList.setData({ content: coInfoMgmt.coHistList });
            },
            onError: () => {},
            showToast: true,
        }),

        // Reset the business user password. !== 업체 사용자 비밀번호를 초기화한다. ==!
        updateCoUsrPwdInit: useFetch({
            api: (data) => APIS.updateCoUsrPwdInit(data),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.ptl.00764", // A temporary password has been sent. !==  임시 비밀번호가 발송되었습니다. ==!
                });
            },
            onError: (error) => {
                console.log(error);
                const data = error.response.data;
                modal.openModal({ content: data.errorMessage });
            },
        }),

        getEmpUsrCoCdList: useFetch({
            api: (data: any) => APIS.getEmpUsrCocdList(data),
            onError: () => toast.showToast({ type: "error", content: "msg.com.00018" }), // An error occurred. !== 에러가 발생했습니다! ==!
        }),

        getCoDcltList: useFetch({
            api: () => APIS.getCoDcltList(coTin),
            onError: () => toast.showToast({ type: "error", content: "msg.com.00018" }), // An error occurred. !== 에러가 발생했습니다! ==!
        }),

        getExternalUserReport: useFetch({
            api: (coTin: string) => {
                return APIS.getExternalUserReport(coTin);
            },
            onSuccess: (data) => {
                // API 조회 성공 후 모달창으로 Pdf 표시
                modal.openModal({
                    type: "pdf",
                    data: data,
                    thumbnail: true,
                    download: true,
                    print: true,
                });
            },
        }),

        // Change the company name to the name stored in TAXPAYER.  !== 업체명을 TAXPAYER 저장 된 업체명으로 변경한다. ==!
        updateCoNm: useFetch({
            api: (data) => APIS.updateCoNm(data),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onClose: () => navigate(0),
                });
            },
            onError: (error) => {
                const data = error.response.data;
                modal.openModal({
                    content: data.errorMessage,
                });
            },
        }),
    };

    const render = {
        coTpList: {
            cell: {
                cstmOfceCdd: (props: any) => {
                    const { value } = props;
                    return `${value}, ${utils.getCodeLabel("cstmCd", value)}`;
                },
            },
        },
        coEmpList: {
            cell: {
                resetPwd: (props: any) => {
                    const { control, rowValues } = props;
                    return (
                        <Button
                            onClick={() => {
                                handler.onPwdBtnClick(rowValues);
                            }}
                        >
                            {t("B_RESET_PWD")}
                        </Button>
                    );
                },
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Company Type List Cell Click  !== 업체 유형 목록 셀 클릭 ==!
        onCoTpListRowClick: (data: any) => form.coTpAplyInfo.setValues(data),
        // Company Employee List Cell Click  !== 업체 직원 목록 셀 클릭 ==!
        onCoEmpListRowClick: (data: any) => {
            form.coEmpInfo.setValues({ ...data, mdfyRsnFile: data.mdfyRsnFile ?? undefined });
            fetch.getEmpUsrCoCdList.fetch(data);
            return;
        },
        // Company History List Cell Click  !== 업체 이력 목록 셀 클릭 ==!
        onCoHistListRowClick: (data: any) => form.coHistInfo.setValues(data),
        // Company Declarant List Cell Click  !== 업체 신고인(CFA) 목록 셀 클릭 ==!
        onCoDcltListRowClick: (data: any) => form.coDcltInfo.setValues(data),

        // Initial Password Button Click !== 비밀번호 초기화 버튼 클릭 ==!
        onPwdBtnClick: (data: any) => {
            modal.openModal({
                content: "msg.ptl.00749", // Do you want to reset your password? !== 비밀번호를 초기화 하시겠습니까? ==!
                onConfirm: () => {
                    fetch.updateCoUsrPwdInit.fetch(data);
                },
            });
        },
        onPrintBtnClick: () => {
            const coTin = form.usrAplyInfo.getValue("coTin");
            fetch.getExternalUserReport.fetch(coTin);
        },
        onChgCoNmBtnClick: async () => {
            const resp = await APIS.getPayerTinNoLst(form.usrAplyInfo.getValue("coTin"), "");

            const tinNoList = resp.data.tinNoList.content;

            if (tinNoList.length == 1) {
                const tinNoInfo = tinNoList[0];
                form.usrAplyInfo.setValue("taxpayerCoNm", tinNoInfo.coNm);

                modal.openModal({
                    content: t("msg.ptl.00833", { 0: form.usrAplyInfo.getValue("coNm"), 1: tinNoInfo.coNm }),
                    onConfirm: () => {
                        if (!comnUtils.isEmpty(form.usrAplyInfo.errors)) {
                            toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                            return;
                        }
                        form.usrAplyInfo.handleSubmit(
                            () => {
                                const usrAplyInfo = form.usrAplyInfo.getValues();
                                fetch.updateCoNm.fetch(usrAplyInfo);
                            },
                            () => {
                                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                            },
                        )();
                    },
                });
            } else {
                toast.showToast({ type: "error", content: "msg.clr.00004" });
            }
        },
    };

    useEffect(() => {
        fetch.getCoInfoMgmt.fetch();
        form.usrAplyInfo.setEditable(false);
        form.coTpAplyInfo.setEditable(false);
        form.coEmpInfo.setEditable(false);
        form.coHistInfo.setEditable(false);
        form.coDcltInfo.setEditable(false);

        grid.coEmpCoCdList.clearData;
        grid.coEmpCoCdList.setEdit("cell", "useYn", false);

        grid.coDcltList.clearData();
        fetch.getCoDcltList.fetch();
    }, []);

    const coTpCd = form.coTpAplyInfo.watch("coTpCd");

    const isFieldVisible = (field: string) => {
        const visibleFields = FIELD_VISIBILITY_MAP.get(coTpCd) || new Set();
        return visibleFields.has(field);
    };

    const isAtchFileVisible = (field: string) => {
        const visibleFields = ATCH_FILE_VISIBILITY_MAP.get(coTpCd) || new Set();
        return visibleFields.has(field);
    };

    const isLcneUseTpCdSelectTrgt = () => {
        return "C" === coTpCd;
    };

    const isCellGroupVisible = FIELD_VISIBILITY_MAP.get(coTpCd)?.size;
    return (
        <Page>
            <Tab
                {...tab.tab}
                onChange={(index) => {
                    // 수정 화면에는 History 탭이 없기 때문에 History 탭은 제외
                    if (index == 3) {
                        setStore(pgeUid, { activeTab: 0 });
                    } else {
                        // Declarant 탭은 3으로 설정
                        if (index == 4) {
                            setStore(pgeUid, { activeTab: 3 });
                        } else {
                            setStore(pgeUid, { activeTab: index });
                        }
                    }
                    // Call the list of departments in an Organization when you click on a tab !== 신규 등록시 부서 탭 클릭시 알림 문구 출력 ==!
                    // handler.onDeptTabClick(index);
                }}
            >
                <Tab.Panel>
                    <Group>
                        <Group.Header>
                            <Group.Title title={"T_CO_INFO"} />
                        </Group.Header>
                        <Group.Body>
                            <Group.Section>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.coTin} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.coNm}></Group.Control>
                                    <Group.Control {...form.usrAplyInfo.schema.inwdCoYn}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.rprsTelno}></Group.Control>
                                    <Group.Control {...form.usrAplyInfo.schema.rprsFaxNo}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.rprsEml}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.coAddr}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.tinStat}></Group.Control>
                                </Group.Row>
                            </Group.Section>
                        </Group.Body>
                    </Group>
                    <Group>
                        <Group.Header>
                            <Group.Title title={t("T_RPPN_INFO")} />
                        </Group.Header>
                        <Group.Body>
                            <Group.Section>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.xtrnUsrIdntfNo} />
                                    <Group.Control {...form.usrAplyInfo.schema.usrNm}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.aplcCntyCd}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.frgnrYn}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.usrMoblNo}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.usrEmlAddr}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.rsdtAddr}></Group.Control>
                                </Group.Row>
                            </Group.Section>
                        </Group.Body>
                    </Group>
                </Tab.Panel>
                <Tab.Panel>
                    <Group>
                        <Group.Header>
                            <Group.Title title={t("T_CO_TP")} />
                        </Group.Header>
                        <Group.Body>
                            <Group.Section>
                                <Grid
                                    {...grid.coTpList.grid}
                                    render={render.coTpList}
                                    onRowClick={handler.onCoTpListRowClick}
                                />
                            </Group.Section>
                            <Group.Section key={form.coTpAplyInfo.schema.key}>
                                <Group.Row>
                                    <Group.Control {...form.coTpAplyInfo.schema.coTpCd} />
                                    <Group.Control {...form.coTpAplyInfo.schema.cstmOfceCd}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.coTpAplyInfo.schema.coCd}></Group.Control>
                                    <Group.Control {...form.coTpAplyInfo.schema.useYn} />
                                </Group.Row>
                                {/* Company Type Name 출력 << 여기서 */}
                                <Group.Row>
                                    <Group.Control {...form.coTpAplyInfo.schema.coTpNm} />
                                    <Group.Control {...form.coTpAplyInfo.schema.tasacCrtfNoStr} />
                                </Group.Row>
                                {isLcneUseTpCdSelectTrgt() && (
                                    <Group.Row>
                                        <Group.Control {...form.coTpAplyInfo.schema.lcneUseTpCd} />
                                    </Group.Row>
                                )}

                                {isCellGroupVisible != null && isCellGroupVisible > 0 && (
                                    <Group.Row>
                                        {isFieldVisible("gainCfrmOmsnYn") && (
                                            <Group.Control {...form.coTpAplyInfo.schema.gainCfrmOmsnYn}></Group.Control>
                                        )}
                                        {isFieldVisible("gaotCfrmOmsnYn") && (
                                            <Group.Control {...form.coTpAplyInfo.schema.gaotCfrmOmsnYn}></Group.Control>
                                        )}
                                        {isFieldVisible("stckCretCtraTpCd") && (
                                            <Group.Control
                                                {...form.coTpAplyInfo.schema.stckCretCtraTpCd}
                                            ></Group.Control>
                                        )}
                                        {isFieldVisible("stckClarCtraTpCd") && (
                                            <Group.Control
                                                {...form.coTpAplyInfo.schema.stckClarCtraTpCd}
                                            ></Group.Control>
                                        )}
                                    </Group.Row>
                                )}
                                {isCellGroupVisible != null && isCellGroupVisible > 0 && (
                                    <Group.Row>
                                        {isFieldVisible("autoCriYn") && (
                                            <Group.Control {...form.coTpAplyInfo.schema.autoCriYn}></Group.Control>
                                        )}
                                        {isFieldVisible("autoCroYn") && (
                                            <Group.Control {...form.coTpAplyInfo.schema.autoCroYn}></Group.Control>
                                        )}
                                        {isFieldVisible("wrhsDpstDays") && (
                                            <Group.Control {...form.coTpAplyInfo.schema.wrhsDpstDays}></Group.Control>
                                        )}
                                        {isFieldVisible("psnWrhsYn") && (
                                            <Group.Control {...form.coTpAplyInfo.schema.psnWrhsYn}></Group.Control>
                                        )}
                                        {isFieldVisible("spcdlWrhsYn") && (
                                            <Group.Control {...form.coTpAplyInfo.schema.spcdlWrhsYn}></Group.Control>
                                        )}
                                        {isFieldVisible("rskCagYn") && (
                                            <Group.Control {...form.coTpAplyInfo.schema.rskCagYn}></Group.Control>
                                        )}
                                    </Group.Row>
                                )}
                                {isAtchFileVisible("bsopId") && (
                                    <Group.Row>
                                        <Group.Control {...form.coTpAplyInfo.schema.bsopId} controlSize={10} />
                                    </Group.Row>
                                )}
                            </Group.Section>
                            <Layout direction="row">
                                <Layout.Left></Layout.Left>
                            </Layout>
                        </Group.Body>
                    </Group>
                </Tab.Panel>
                <Tab.Panel>
                    <Group>
                        <Group.Header>
                            <Group.Title title={t("T_CO_EMP_LIST")} />
                        </Group.Header>
                        <Group.Body>
                            <Group.Section>
                                <Grid
                                    {...grid.coEmpList.grid}
                                    render={render.coEmpList}
                                    onRowClick={handler.onCoEmpListRowClick}
                                />
                            </Group.Section>
                            <Group.Section>
                                <Group.Row>
                                    <Group.Control {...form.coEmpInfo.schema.xtrnUsrIdntfNo} />
                                    <Group.Control {...form.coEmpInfo.schema.usrStatCd} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.coEmpInfo.schema.usrNm} />
                                    <Group.Control {...form.coEmpInfo.schema.coTin} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.coEmpInfo.schema.frgnrYn} />
                                    <Group.Control {...form.coEmpInfo.schema.aplcCntyCd} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.coEmpInfo.schema.coRppnYn} />
                                    <Group.Control {...form.coEmpInfo.schema.cstmOfceCd} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.coEmpInfo.schema.usrMoblNo} />
                                    <Group.Control {...form.coEmpInfo.schema.usrEmlAddr} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.coEmpInfo.schema.rsdtAddr} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.coEmpInfo.schema.mdfyRsn} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.coEmpInfo.schema.mdfyRsnFile} />
                                </Group.Row>
                            </Group.Section>

                            {form.coEmpInfo.getValue("coRppnYn") && form.coEmpInfo.getValue("coRppnYn") != "Y" && (
                                <Group.Section>
                                    <Group.Title title={"T_EMP_CO_CD_LIST"} />
                                    <Grid
                                        {...grid.coEmpCoCdList.grid}
                                        data={fetch.getEmpUsrCoCdList.data?.empUsrCoCdList}
                                    />
                                </Group.Section>
                            )}
                        </Group.Body>
                    </Group>
                </Tab.Panel>
                <Tab.Panel>
                    <Group>
                        <Group.Header>
                            <Group.Title title={t("T_CO_HIST_LIST")} />
                        </Group.Header>
                        <Group.Body>
                            <Group.Section>
                                <Grid {...grid.coHistList.grid} onRowClick={handler.onCoHistListRowClick} />
                            </Group.Section>
                            <Group.Section>
                                <Group.Row>
                                    <Group.Control {...form.coHistInfo.schema.coTin} />
                                    <Group.Control {...form.coHistInfo.schema.histDtm} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.coHistInfo.schema.coNm} />
                                    <Group.Control {...form.coHistInfo.schema.inwdCoYn} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.coHistInfo.schema.rprsTelno} />
                                    <Group.Control {...form.coHistInfo.schema.rprsFaxNo} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.coHistInfo.schema.rprsEml} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.coHistInfo.schema.coAddr} />
                                </Group.Row>
                            </Group.Section>
                        </Group.Body>
                    </Group>
                </Tab.Panel>
                <Tab.Panel>
                    <Group>
                        <Group.Body>
                            <Layout>
                                <Layout.Left>
                                    <Group.Title title={"T_CFA_LIST"} titleSize={1}></Group.Title>
                                </Layout.Left>
                            </Layout>
                            <Group.Section>
                                <Layout gap={4} direction="col">
                                    <Grid
                                        {...grid.coDcltList.grid}
                                        data={fetch.getCoDcltList.data?.coDcltList}
                                        onRowClick={handler.onCoDcltListRowClick}
                                    />
                                </Layout>
                            </Group.Section>
                            <Group.Section>
                                <Group.Row>
                                    <Group.Control {...form.coDcltInfo.schema.dcltTin} />
                                    <Group.Control {...form.coDcltInfo.schema.dcltNm} edit={false} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.coDcltInfo.schema.dcltAddr} edit={false} />
                                    <Group.Control {...form.coDcltInfo.schema.dcltTelno} edit={false} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.coDcltInfo.schema.dcltFaxNo} edit={false} />
                                    <Group.Control {...form.coDcltInfo.schema.useYn} />
                                </Group.Row>
                            </Group.Section>
                        </Group.Body>
                    </Group>
                </Tab.Panel>
            </Tab>
            <Layout direction="row">
                <Layout direction="row">
                    <Layout.Left>
                        <Button
                            role="list"
                            onClick={() => {
                                navigate(URLS.ptli0905001q);
                            }}
                        ></Button>
                    </Layout.Left>
                    <Layout.Right>
                        <Button onClick={handler.onPrintBtnClick}>View PDF</Button>
                        <Button
                            color="warning"
                            onClick={() => {
                                handler.onChgCoNmBtnClick();
                            }}
                        >
                            {t("B_CO_NM_MDFY")}
                        </Button>
                        <Button
                            role="edit"
                            onClick={() => {
                                navigate(`${URLS.ptli0905003s}/${coTin}`);
                            }}
                        ></Button>
                    </Layout.Right>
                </Layout>
            </Layout>
        </Page>
    );
};
